import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { format } from 'date-fns';
import { Package, Calendar as CalendarIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientRepacks = ({ token, user, trackings, repacks, onRefresh, onNavigate }) => {
  // Form states
  const [selectedTrackings, setSelectedTrackings] = useState([]);
  const [shippingMethod, setShippingMethod] = useState('');
  const [repackNotes, setRepackNotes] = useState('');
  const [trackingSearchFilter, setTrackingSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit mode states
  const [editingRepackId, setEditingRepackId] = useState(null);
  const [editingRepackData, setEditingRepackData] = useState(null);

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRepack, setViewingRepack] = useState(null);
  const [editRepackTrackings, setEditRepackTrackings] = useState([]);
  const [editRepackNotes, setEditRepackNotes] = useState('');
  const [editRepackMethod, setEditRepackMethod] = useState('');

  // Filters for my-repacks
  const [repackStatusFilter, setRepackStatusFilter] = useState('all');
  const [repackDateFrom, setRepackDateFrom] = useState(null);
  const [repackDateTo, setRepackDateTo] = useState(null);

  // Current view: 'request' or 'list'
  const [view, setView] = useState('list');

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
  const availableTrackings = trackings.filter(t => t.status === 'received');

  const getTrackingInfo = (trackingId) => {
    return trackings.find(t => t.id === trackingId) || { tracking_number: trackingId, description: 'No encontrado' };
  };

  const handleTrackingSelection = (trackingId) => {
    setSelectedTrackings(prev => prev.includes(trackingId) ? prev.filter(id => id !== trackingId) : [...prev, trackingId]);
  };

  const handleCreateRepack = async (e) => {
    e.preventDefault();
    if (selectedTrackings.length === 0) { alert('Por favor selecciona al menos un tracking'); return; }
    if (!shippingMethod) { alert('Por favor selecciona un metodo de envio'); return; }
    
    try {
      if (editingRepackId) {
        await axios.put(`${API}/client/repacks/${editingRepackId}`, { tracking_ids: selectedTrackings, shipping_method: shippingMethod, notes: repackNotes || null }, axiosConfig);
        setEditingRepackId(null); setEditingRepackData(null);
        setSelectedTrackings([]); setShippingMethod(''); setRepackNotes('');
        onRefresh();
        alert('Solicitud de reempaque actualizada exitosamente!');
        setView('list');
      } else {
        await axios.post(`${API}/client/repacks`, { tracking_ids: selectedTrackings, shipping_method: shippingMethod, notes: repackNotes || null }, axiosConfig);
        setSelectedTrackings([]); setShippingMethod(''); setRepackNotes('');
        onRefresh();
        alert('Solicitud de repack creada exitosamente! El Master procesara tu solicitud.');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEditRepack = (repack) => {
    if (repack.status === 'requested') {
      setEditingRepackId(repack.id);
      setEditingRepackData(repack);
      setSelectedTrackings(repack.tracking_ids || []);
      setShippingMethod(repack.shipping_method || '');
      setRepackNotes(repack.notes || '');
      setView('request');
    } else {
      setViewingRepack(repack);
      setEditRepackTrackings(repack.tracking_ids || []);
      setEditRepackNotes(repack.notes || '');
      setEditRepackMethod(repack.shipping_method || '');
      setShowViewModal(true);
    }
  };

  const cancelEditMode = () => {
    setEditingRepackId(null); setEditingRepackData(null);
    setSelectedTrackings([]); setShippingMethod(''); setRepackNotes('');
    setView('list');
  };

  const toggleEditRepackTracking = (trackingId) => {
    setEditRepackTrackings(prev => prev.includes(trackingId) ? prev.filter(id => id !== trackingId) : [...prev, trackingId]);
  };

  const handleSaveEditRepack = async () => {
    if (!viewingRepack) return;
    if (editRepackTrackings.length === 0) { alert('Debes seleccionar al menos un paquete'); return; }
    try {
      await axios.put(`${API}/client/repacks/${viewingRepack.id}`, { tracking_ids: editRepackTrackings, shipping_method: editRepackMethod, notes: editRepackNotes }, axiosConfig);
      setShowViewModal(false); setViewingRepack(null); onRefresh();
      alert('Solicitud de reempaque actualizada');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteRepack = async (repack) => {
    if (!repack || repack.status !== 'requested') { alert('Solo puedes eliminar solicitudes en estado "Solicitado"'); return; }
    const trackingCount = repack.tracking_ids?.length || 0;
    if (!window.confirm(`Estas seguro de eliminar esta solicitud de reempaque?\n\n${trackingCount} paquete(s) seran devueltos a tu lista de paquetes disponibles para reempacar.`)) return;
    try {
      await axios.delete(`${API}/client/repacks/${repack.id}`, axiosConfig);
      onRefresh();
      alert(`Solicitud eliminada. ${trackingCount} paquete(s) devuelto(s) a tu lista de paquetes.`);
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredRepacks = repacks.filter(r => {
    const matchesStatus = repackStatusFilter === 'all' || r.status === repackStatusFilter;
    const repackDate = new Date(r.created_at);
    const matchesDateFrom = !repackDateFrom || repackDate >= repackDateFrom;
    const matchesDateTo = !repackDateTo || repackDate <= repackDateTo;
    return matchesStatus && matchesDateFrom && matchesDateTo;
  });

  // Get trackings available for selection
  const editingTrackingIds = editingRepackData?.tracking_ids || [];
  const trackingsInCurrentRepack = editingRepackId ? trackings.filter(t => editingTrackingIds.includes(t.id)) : [];
  const allAvailableForSelection = editingRepackId
    ? [...availableTrackings, ...trackingsInCurrentRepack.filter(t => !availableTrackings.some(a => a.id === t.id))]
    : availableTrackings;
  const filteredForRepack = allAvailableForSelection.filter(t => {
    const matchesSearch = !trackingSearchFilter || t.tracking_number.toLowerCase().includes(trackingSearchFilter.toLowerCase()) || (t.description && t.description.toLowerCase().includes(trackingSearchFilter.toLowerCase()));
    const matchesStatus2 = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus2;
  });

  return { view, setView, renderRequest: () => (
    <div className="space-y-4">
      {editingRepackId && (
        <Card className="border-2 border-orange-400 bg-orange-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div><p className="font-semibold text-orange-800">Modo Edicion</p><p className="text-sm text-orange-700">Estas editando una solicitud existente. Puedes agregar o quitar paquetes de tu seleccion.</p></div>
              </div>
              <Button variant="outline" onClick={cancelEditMode} className="border-orange-400 text-orange-700 hover:bg-orange-100" data-testid="cancel-edit-btn">Cancelar Edicion</Button>
            </div>
          </CardContent>
        </Card>
      )}
      <Card><CardContent className="pt-6"><div className="bg-blue-50 border border-blue-200 p-4 rounded"><p className="text-sm text-blue-800"><strong>Instrucciones:</strong> {editingRepackId ? 'Modifica los paquetes seleccionados y haz clic en "Actualizar Solicitud" para guardar los cambios.' : 'Debe seleccionar por lo menos un paquete para generar la solicitud de reempaque.'}</p></div></CardContent></Card>
      <Card>
        <CardHeader><CardTitle data-testid="repack-form-title">{editingRepackId ? 'Editar Solicitud de Reempaque' : 'Nueva Solicitud de Reempaque'}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleCreateRepack} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Metodo de Envio *</Label>
                <Select value={shippingMethod} onValueChange={setShippingMethod} required>
                  <SelectTrigger data-testid="shipping-method-select"><SelectValue placeholder="Seleccionar metodo" /></SelectTrigger>
                  <SelectContent><SelectItem value="Aereo">Aereo</SelectItem><SelectItem value="Maritimo">Maritimo</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Instrucciones Especiales</Label>
                <Textarea value={repackNotes} onChange={(e) => setRepackNotes(e.target.value)} placeholder="Instrucciones adicionales..." rows={1} className="resize-none" data-testid="repack-notes-input" />
              </div>
            </div>
            <div className="border-t pt-4">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div><Label className="text-xs">Buscar Tracking</Label><Input placeholder="Buscar..." value={trackingSearchFilter} onChange={(e) => setTrackingSearchFilter(e.target.value)} className="h-9" /></div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="pending">Pendiente</SelectItem><SelectItem value="in_warehouse">En almacen</SelectItem>{editingRepackId && <SelectItem value="in_repack">En reempaque</SelectItem>}</SelectContent></Select>
                </div>
                <div className="col-span-2 flex items-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setSelectedTrackings(filteredForRepack.map(t => t.id))} className="h-9">Seleccionar Todos</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSelectedTrackings([])} className="h-9">Deseleccionar Todos</Button>
                </div>
              </div>
            </div>
            {filteredForRepack.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="p-2 text-left w-12"><Checkbox checked={selectedTrackings.length === filteredForRepack.length && filteredForRepack.length > 0} onClick={(e) => { e.stopPropagation(); selectedTrackings.length === filteredForRepack.length ? setSelectedTrackings([]) : setSelectedTrackings(filteredForRepack.map(t => t.id)); }} /></th>
                        <th className="p-2 text-left font-semibold">Tracking</th>
                        <th className="p-2 text-left font-semibold">Fecha</th>
                        <th className="p-2 text-left font-semibold">Descripcion</th>
                        <th className="p-2 text-left font-semibold">Peso (lb)</th>
                        <th className="p-2 text-left font-semibold">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredForRepack.map((tracking, index) => {
                        const isInCurrentRepack = editingTrackingIds.includes(tracking.id);
                        return (
                          <tr key={tracking.id} className={`border-b ${isInCurrentRepack ? 'bg-orange-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors`} onClick={() => handleTrackingSelection(tracking.id)} data-testid={`tracking-row-${tracking.id}`}>
                            <td className="p-2" onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedTrackings.includes(tracking.id)} onClick={(e) => { e.stopPropagation(); handleTrackingSelection(tracking.id); }} /></td>
                            <td className="p-2 font-medium text-blue-600">{tracking.tracking_number}{isInCurrentRepack && <span className="ml-2 text-xs bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded">En esta solicitud</span>}</td>
                            <td className="p-2 text-gray-600">{new Date(tracking.created_at).toLocaleDateString()}</td>
                            <td className="p-2 text-gray-700">{tracking.description || 'Sin descripcion'}</td>
                            <td className="p-2 text-gray-700">{tracking.weight || 'N/A'}</td>
                            <td className="p-2"><span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${tracking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : tracking.status === 'in_repack' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{tracking.status === 'pending' ? 'Pendiente' : tracking.status === 'in_repack' ? 'En reempaque' : tracking.status === 'received' ? 'Recibido' : 'En almacen'}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-100 px-3 py-2 text-xs text-gray-600 border-t">{selectedTrackings.length} de {filteredForRepack.length} paquete(s) seleccionado(s)</div>
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-8 text-center"><Package className="mx-auto h-10 w-10 text-gray-300 mb-2" /><p className="text-gray-500">Lo siento, no se encontraron paquetes</p></div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              {editingRepackId && <Button type="button" variant="outline" onClick={cancelEditMode}>Cancelar</Button>}
              <Button type="submit" disabled={selectedTrackings.length === 0 || !shippingMethod} className={editingRepackId ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"} data-testid="submit-repack-btn">{editingRepackId ? 'Actualizar Solicitud' : 'Solicitar Reempaque'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  ), renderList: () => (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Mis Reempaques</CardTitle><CardDescription>Historial de tus solicitudes de reempaque</CardDescription></CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3 mb-4">
            <div><Label className="text-xs">Tipo de Envio</Label><Select defaultValue="all"><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="Aereo">Aereo</SelectItem><SelectItem value="Maritimo">Maritimo</SelectItem></SelectContent></Select></div>
            <div>
              <Label className="text-xs">Desde</Label>
              <Popover><PopoverTrigger asChild><Button variant="outline" className="h-9 w-full justify-start text-left font-normal"><CalendarIcon className="mr-2 h-4 w-4" />{repackDateFrom ? format(repackDateFrom, "dd/MM/yyyy") : "Seleccionar"}</Button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={repackDateFrom} onSelect={setRepackDateFrom} initialFocus /></PopoverContent></Popover>
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Popover><PopoverTrigger asChild><Button variant="outline" className="h-9 w-full justify-start text-left font-normal"><CalendarIcon className="mr-2 h-4 w-4" />{repackDateTo ? format(repackDateTo, "dd/MM/yyyy") : "Seleccionar"}</Button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={repackDateTo} onSelect={setRepackDateTo} initialFocus /></PopoverContent></Popover>
            </div>
            <div><Label className="text-xs">Tipo</Label><Select defaultValue="paquete"><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="paquete">Paquete</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Estatus</Label><Select value={repackStatusFilter} onValueChange={setRepackStatusFilter}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="requested">Solicitado</SelectItem><SelectItem value="processing">En Proceso</SelectItem><SelectItem value="completed">Completado</SelectItem></SelectContent></Select></div>
          </div>
          {filteredRepacks.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="p-2 text-left font-semibold text-xs">Numero</th>
                      <th className="p-2 text-left font-semibold text-xs">Tipo</th>
                      <th className="p-2 text-left font-semibold text-xs">Cliente</th>
                      <th className="p-2 text-left font-semibold text-xs">Solicitado</th>
                      <th className="p-2 text-left font-semibold text-xs">Estatus</th>
                      <th className="p-2 text-left font-semibold text-xs">F. de Operacion</th>
                      <th className="p-2 text-left font-semibold text-xs">Tipo de Envio</th>
                      <th className="p-2 text-left font-semibold text-xs">Tracking</th>
                      <th className="p-2 text-left font-semibold text-xs">Instrucciones</th>
                      <th className="p-2 text-left font-semibold text-xs">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRepacks.map((repack, index) => (
                      <tr key={repack.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                        <td className="p-2 font-medium text-blue-600">REPACK{9700 + index}</td>
                        <td className="p-2 text-gray-700">Paquete</td>
                        <td className="p-2 text-gray-700">{user?.full_name || 'Cliente'}</td>
                        <td className="p-2 text-gray-600 text-xs">{new Date(repack.created_at).toLocaleString()}</td>
                        <td className="p-2"><span className={`inline-block px-2 py-1 rounded text-xs font-semibold uppercase ${repack.status === 'requested' ? 'bg-yellow-100 text-yellow-700' : repack.status === 'processing' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{repack.status === 'requested' ? 'SOLICITADO' : repack.status === 'processing' ? 'EN PROCESO' : 'COMPLETADO'}</span></td>
                        <td className="p-2 text-gray-500 text-xs">{repack.status === 'completed' ? new Date(repack.updated_at).toLocaleDateString() : 'N/A'}</td>
                        <td className="p-2 text-gray-700">{repack.shipping_method}</td>
                        <td className="p-2 text-gray-600 text-xs max-w-xs"><div className="truncate">{repack.tracking_ids.slice(0, 2).join(', ')}{repack.tracking_ids.length > 2 && ` +${repack.tracking_ids.length - 2} mas`}</div></td>
                        <td className="p-2 text-gray-600 text-xs max-w-xs"><div className="truncate">{repack.notes || 'Sin instrucciones'}</div></td>
                        <td className="p-2">
                          {repack.status === 'requested' ? (
                            <div className="flex gap-1">
                              <button onClick={() => openEditRepack(repack)} className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium" data-testid={`edit-repack-${repack.id}`}>Editar</button>
                              <button onClick={() => handleDeleteRepack(repack)} className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs font-medium" data-testid={`delete-repack-${repack.id}`}>Eliminar</button>
                            </div>
                          ) : (
                            <button onClick={() => openEditRepack(repack)} className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-xs font-medium">Ver</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-100 px-3 py-2 text-xs text-gray-600 border-t">Mostrando {filteredRepacks.length} de {repacks.length} reempaque(s)</div>
            </div>
          ) : (
            <div className="border-2 border-dashed rounded-lg p-12 text-center"><Package className="mx-auto h-12 w-12 text-gray-300 mb-3" /><p className="text-gray-500 text-lg">No tienes solicitudes de reempaque</p><p className="text-gray-400 text-sm mt-2">Ve a "Solicitar Reempaque" para crear una nueva solicitud</p></div>
          )}
        </CardContent>
      </Card>

      {/* View/Edit Modal for non-requested repacks */}
      {showViewModal && viewingRepack && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b bg-blue-600 text-white rounded-t-lg">
              <h3 className="text-lg font-bold">{viewingRepack.status === 'requested' ? 'Editar Solicitud de Reempaque' : 'Ver Solicitud de Reempaque'}</h3>
              <p className="text-blue-100 text-sm">{viewingRepack.status === 'requested' ? 'Modifica los paquetes seleccionados para tu reempaque' : 'Esta solicitud ya esta siendo procesada y no se puede editar'}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4"><div className="grid grid-cols-2 gap-4 text-sm"><div><span className="text-gray-500">Estado:</span><span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${viewingRepack.status === 'requested' ? 'bg-yellow-100 text-yellow-800' : viewingRepack.status === 'processing' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{viewingRepack.status === 'requested' ? 'Solicitado' : viewingRepack.status === 'processing' ? 'En Proceso' : 'Completado'}</span></div><div><span className="text-gray-500">Metodo de envio:</span><span className="ml-2 font-medium">{viewingRepack.shipping_method || 'No especificado'}</span></div></div></div>
              <div>
                <label className="block text-sm font-semibold mb-2">Paquetes incluidos ({editRepackTrackings.length})</label>
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {trackings.filter(t => t.status === 'received' || editRepackTrackings.includes(t.id)).map(tracking => {
                    const isSelected = editRepackTrackings.includes(tracking.id);
                    const isDisabled = viewingRepack.status !== 'requested';
                    return (
                      <div key={tracking.id} className={`flex items-center p-3 border-b last:border-b-0 ${isSelected ? 'bg-blue-50' : 'bg-white'} ${!isDisabled ? 'hover:bg-gray-50 cursor-pointer' : ''}`} onClick={() => !isDisabled && toggleEditRepackTracking(tracking.id)}>
                        <input type="checkbox" checked={isSelected} onChange={() => !isDisabled && toggleEditRepackTracking(tracking.id)} disabled={isDisabled} className="mr-3 h-4 w-4" />
                        <div className="flex-1"><p className="font-mono text-sm font-semibold">{tracking.tracking_number}</p><p className="text-xs text-gray-500">{tracking.description || 'Sin descripcion'}</p></div>
                        <div className="text-right text-xs text-gray-500">{tracking.weight ? `${tracking.weight} lbs` : ''}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {viewingRepack.status === 'requested' && (
                <div><label className="block text-sm font-semibold mb-2">Metodo de Envio</label><select value={editRepackMethod} onChange={(e) => setEditRepackMethod(e.target.value)} className="w-full border rounded-lg p-2"><option value="">Seleccionar metodo</option><option value="aereo">Aereo</option><option value="maritimo">Maritimo</option></select></div>
              )}
              <div><label className="block text-sm font-semibold mb-2">Instrucciones Especiales</label><textarea value={editRepackNotes} onChange={(e) => setEditRepackNotes(e.target.value)} disabled={viewingRepack.status !== 'requested'} className="w-full border rounded-lg p-2 text-sm" rows={3} placeholder="Instrucciones para el reempaque..." /></div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
              <button onClick={() => { setShowViewModal(false); setViewingRepack(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-100">{viewingRepack.status === 'requested' ? 'Cancelar' : 'Cerrar'}</button>
              {viewingRepack.status === 'requested' && <button onClick={handleSaveEditRepack} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar Cambios</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  )};
};

export default ClientRepacks;
